import React from 'react'
import { IconLayer } from '@deck.gl/layers/typed'
import { Collation } from 'common/models/typings/Collation'
import { COLORS } from 'constants/COLORS'

export type ExtendedCollation = Collation & {
  tourId: string | number
  tourColor: string
  isSelected: boolean
}

interface MarkerLayerProps {
  allCollations: ExtendedCollation[]
  selectedMarkers: string[]
  onMarkerClick: (collation: ExtendedCollation) => void
}

const svgCache = new Map<string, string>()

const generateMarkerSVG = (
  color: string,
  position: number,
  isSelected: boolean,
) => {
  const key = `${color}-${position}-${isSelected}`

  if (svgCache.has(key)) {
    return svgCache.get(key) || ''
  }

  const fillColor = isSelected ? COLORS.GREEN_1 : color
  const strokeColor = 'rgba(0, 0, 0, 0.3)'

  const svg = `<svg width="44" height="56" viewBox="0 0 44 56" fill="none" xmlns="http://www.w3.org/2000/svg" shape-rendering="geometricPrecision" text-rendering="geometricPrecision">
    <defs>
      <filter id="shadow-${key.replace(
        /[^a-zA-Z0-9]/g,
        '',
      )}" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur in="SourceAlpha" stdDeviation="1.5"/>
        <feOffset dx="0" dy="2" result="offsetblur"/>
        <feComponentTransfer>
          <feFuncA type="linear" slope="0.3"/>
        </feComponentTransfer>
        <feMerge>
          <feMergeNode/>
          <feMergeNode in="SourceGraphic"/>
        </feMerge>
      </filter>
      <filter id="text-shadow-${key.replace(
        /[^a-zA-Z0-9]/g,
        '',
      )}" x="-50%" y="-50%" width="200%" height="200%">
        <feDropShadow dx="0" dy="1" stdDeviation="1" flood-color="#000" flood-opacity="0.4"/>
      </filter>
    </defs>
    <g filter="url(#shadow-${key.replace(/[^a-zA-Z0-9]/g, '')})">
      <path d="M7.57462 32.6434C-0.392062 25.2672 -0.392062 13.3079 7.57462 5.93162C15.5413 -1.44466 28.4578 -1.44466 36.4246 5.93162C44.3912 13.3079 44.3912 25.2672 36.4246 32.6434L21.9996 45.9994L7.57462 32.6434Z" fill="${fillColor}" stroke="${strokeColor}" stroke-width="1" stroke-linejoin="round"/>
    </g>
    <text text-anchor="middle" fill="#fff" font-size="18" font-family="ClanPro-WideBold, Arial, sans-serif" font-weight="bold" x="22" y="23" dominant-baseline="middle" filter="url(#text-shadow-${key.replace(
      /[^a-zA-Z0-9]/g,
      '',
    )})">${position}</text>
  </svg>`

  svgCache.set(key, svg)
  return svg
}

export default function createMarkerLayer({
  allCollations,
  selectedMarkers,
  onMarkerClick,
}: MarkerLayerProps) {
  const layer = new IconLayer<ExtendedCollation>({
    id: 'IconLayer',
    data: allCollations,
    getIcon: (d) => {
      const svg = generateMarkerSVG(d.tourColor, d.position, d.isSelected)
      return {
        url: `data:image/svg+xml;base64,${btoa(svg)}`,
        height: 56,
        width: 44,
        anchorX: 22,
        anchorY: 46,
      }
    },
    getPosition: (d) => [
      d.recipient.location.longitude,
      d.recipient.location.latitude,
    ],
    getSize: 36,
    getColor: (d) => (d.isSelected ? [255, 0, 0, 255] : [0, 0, 255, 255]),
    pickable: true,
    onClick: (info) => {
      if (info.object) {
        onMarkerClick(info.object)
      }
    },
    updateTriggers: {
      getIcon: [selectedMarkers],
    },
  })

  return layer
}
